import React, { useState, useEffect } from 'react';
import { Operation, Team } from '../types';
import { formatApiDateTimeForDisplay } from '../utils/dateTime';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faSave, faTimes, faFilePdf, faUsers, faMapMarkerAlt, faTag, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

interface OperationOverviewProps {
  operation: Operation;
  teams: Team[];
  onGenerateReport: () => void;
  onUpdateOperation?: (updatedOperation: Partial<Operation>) => Promise<void>;
}

const OperationOverview: React.FC<OperationOverviewProps> = ({
  operation,
  teams,
  onGenerateReport,
  onUpdateOperation
}) => {
  const { user } = useAuth();
  
  // Check if user can generate reports
  const canGenerateReports = user?.permissions?.['firehub-super-admin'] || user?.permissions?.['asm-manage-reports'];
  const canManageOperations = user?.permissions?.['firehub-super-admin'] || user?.permissions?.['asm-manage-operations'];
  const [isEditing, setIsEditing] = useState(false);
  const [editedLocation, setEditedLocation] = useState(operation.location);
  const [editedCategory, setEditedCategory] = useState(operation.category);
  const [isSaving, setIsSaving] = useState(false);

  // Update local state when operation props change
  useEffect(() => {
    setEditedLocation(operation.location || '');
    setEditedCategory(operation.category || '');
  }, [operation.location, operation.category]);
  const [categories, setCategories] = useState<any[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | undefined>(undefined);

  const handleSave = async () => {
    if (!onUpdateOperation) return;
    
    setIsSaving(true);
    try {
      await onUpdateOperation({
        location: editedLocation,
        category: editedCategory,
        categoryID: selectedCategoryId
      });
      setIsEditing(false);
      setShowCategoryDropdown(false);
    } catch (error) {
      console.error('Error updating operation:', error);
      // Don't show error to user since data is saved locally
      // Just close the editing mode
      setIsEditing(false);
      setShowCategoryDropdown(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditedLocation(operation.location);
    setEditedCategory(operation.category);
    setIsEditing(false);
    setShowCategoryDropdown(false);
  };

  // Load categories from API - only those defined as Atemschutzkategorien for the org
  useEffect(() => {
    const loadCategories = async () => {
      try {
        setLoadingCategories(true);
        
        // Get user's orgID from context or localStorage
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const orgID = user.orgID;
        
        if (!orgID) {
          console.error('No orgID found for user');
          setCategories([]);
          return;
        }
        
        const response = await api.post('/api/v1/operations/types/by-org', { orgID });
        
        if (response.data && Array.isArray(response.data)) {
          // Store full category objects for later use
          setCategories(response.data);
        }
      } catch (error) {
        console.error('Error loading organization categories:', error);
        // Fallback to empty array
        setCategories([]);
      } finally {
        setLoadingCategories(false);
      }
    };

    loadCategories();
  }, []);

  const handleCategorySelect = (category: any) => {
    setEditedCategory(category.name || category.category || category);
    setSelectedCategoryId(category.id);
    setShowCategoryDropdown(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.category-dropdown')) {
        setShowCategoryDropdown(false);
      }
    };

    if (showCategoryDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCategoryDropdown]);

  return (
    <div>
      {/* Operation Details */}
      <div style={{
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '14px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)', border: '1px solid #ececec',
        marginBottom: '20px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, color: '#333' }}>Einsatz-Details</h2>
          <div style={{ display: 'flex', gap: '10px' }}>
            {!isEditing && canManageOperations && operation.lockStatus !== 2 && (
              <button
                onClick={() => setIsEditing(true)}
                style={{
                  padding: '8px 12px',
                  backgroundColor: '#2196f3',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <FontAwesomeIcon icon={faEdit} />
                Bearbeiten
              </button>
            )}
            {operation.lockStatus === 2 && (
              <button
                disabled
                style={{
                  padding: '8px 12px',
                  backgroundColor: '#666',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'not-allowed',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  opacity: 0.7
                }}
                title="Versiegelte Einsätze können nicht bearbeitet werden"
              >
                <FontAwesomeIcon icon={faEdit} />
                Bearbeiten
              </button>
            )}
            {canGenerateReports && (
              <button
                onClick={onGenerateReport}
                disabled={!operation.asm_teams}
                style={{
                  padding: '8px 12px',
                  backgroundColor: operation.asm_teams ? '#c00' : '#666',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: operation.asm_teams ? 'pointer' : 'not-allowed',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  opacity: operation.asm_teams ? 1 : 0.7
                }}
                title={operation.asm_teams ? "PDF Bericht generieren" : "PDF Export nur für Atemschutzeinsätze verfügbar"}
              >
                <FontAwesomeIcon icon={faFilePdf} />
                PDF Bericht
              </button>
            )}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
          <div style={{ padding: '15px', backgroundColor: '#f9f9f9', borderRadius: '14px' }}>
            <strong style={{ color: '#666', fontSize: '12px', textTransform: 'uppercase' }}>Einsatznummer</strong>
            <div style={{ fontSize: '16px', fontWeight: 'bold', marginTop: '5px' }}>{operation.opsNumber}</div>
          </div>
          
          <div style={{ padding: '15px', backgroundColor: '#f9f9f9', borderRadius: '14px' }}>
            <strong style={{ color: '#666', fontSize: '12px', textTransform: 'uppercase' }}>Ort</strong>
            {isEditing && operation.lockStatus !== 2 ? (
              <input
                type="text"
                value={editedLocation}
                onChange={(e) => setEditedLocation(e.target.value)}
                style={{
                  width: '100%',
                  marginTop: '5px',
                  padding: '8px 12px',
                  border: '2px solid #2196f3',
                  borderRadius: '6px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
                placeholder="Standort eingeben..."
              />
            ) : (
              <div style={{ fontSize: '16px', marginTop: '5px', display: 'flex', alignItems: 'center' }}>
                <FontAwesomeIcon icon={faMapMarkerAlt} style={{ marginRight: '8px', color: '#666' }} />
                {operation.location || 'Kein Standort'}
              </div>
            )}
          </div>
          
          <div style={{ padding: '15px', backgroundColor: '#f9f9f9', borderRadius: '14px', position: 'relative' }}>
            <strong style={{ color: '#666', fontSize: '12px', textTransform: 'uppercase' }}>Kategorie</strong>
            {isEditing && operation.lockStatus !== 2 ? (
              <div style={{ position: 'relative' }} className="category-dropdown">
                <button
                  onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                  style={{
                    width: '100%',
                    marginTop: '5px',
                    padding: '8px 12px',
                    border: '2px solid #2196f3',
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    boxSizing: 'border-box'
                  }}
                >
                  <span style={{ color: editedCategory ? '#333' : '#999' }}>
                    {editedCategory || 'Kategorie auswählen...'}
                  </span>
                  <FontAwesomeIcon 
                    icon={faChevronDown} 
                    style={{ 
                      color: '#666',
                      transform: showCategoryDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s ease'
                    }} 
                  />
                </button>
                
                {showCategoryDropdown && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: 'white',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                    zIndex: 1000,
                    maxHeight: '200px',
                    overflowY: 'auto'
                  }}>
                    {loadingCategories ? (
                      <div style={{ padding: '12px', textAlign: 'center', color: '#666' }}>
                        Kategorien werden geladen...
                      </div>
                    ) : categories.length > 0 ? (
                      categories.map((category, index) => (
                        <button
                          key={category.id || index}
                          onClick={() => handleCategorySelect(category)}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: 'none',
                            backgroundColor: 'transparent',
                            cursor: 'pointer',
                            textAlign: 'left',
                            fontSize: '14px',
                            color: '#333',
                            borderBottom: index < categories.length - 1 ? '1px solid #f0f0f0' : 'none'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#f5f5f5';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          {category.name || category.category || category}
                        </button>
                      ))
                    ) : (
                      <div style={{ padding: '12px', textAlign: 'center', color: '#666' }}>
                        Keine Kategorien verfügbar
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div style={{ fontSize: '16px', marginTop: '5px', display: 'flex', alignItems: 'center' }}>
                <FontAwesomeIcon icon={faTag} style={{ marginRight: '8px', color: '#666' }} />
                {operation.category || 'Keine Kategorie'}
              </div>
            )}
          </div>
          
          <div style={{ padding: '15px', backgroundColor: '#f9f9f9', borderRadius: '14px' }}>
            <strong style={{ color: '#666', fontSize: '12px', textTransform: 'uppercase' }}>Erstellt</strong>
            <div style={{ fontSize: '16px', marginTop: '5px' }}>
              {formatApiDateTimeForDisplay(operation.created)}
            </div>
          </div>
          
          <div style={{ padding: '15px', backgroundColor: '#f9f9f9', borderRadius: '14px' }}>
            <strong style={{ color: '#666', fontSize: '12px', textTransform: 'uppercase' }}>Trupps</strong>
            <div style={{ fontSize: '16px', marginTop: '5px', display: 'flex', alignItems: 'center' }}>
              <FontAwesomeIcon icon={faUsers} style={{ marginRight: '8px', color: '#666' }} />
              {teams.length}
            </div>
          </div>
          
          <div style={{ padding: '15px', backgroundColor: '#f9f9f9', borderRadius: '14px' }}>
            <strong style={{ color: '#666', fontSize: '12px', textTransform: 'uppercase' }}>Status</strong>
            <div style={{ marginTop: '5px' }}>
              <span style={{
                backgroundColor: operation.lockStatus === 1 ? '#ff9800' : '#4caf50',
                color: 'white',
                padding: '6px 12px',
                borderRadius: '20px',
                fontSize: '12px',
                fontWeight: 'bold',
                display: 'inline-block'
              }}>
                {operation.lockStatus === 1 ? 'Geschlossen' : 'Offen'}
              </span>
            </div>
          </div>
        </div>

        {isEditing && operation.lockStatus !== 2 && (
          <div style={{ 
            display: 'flex', 
            gap: '12px', 
            marginTop: '25px',
            padding: '20px',
            backgroundColor: '#f8f9fa',
            borderRadius: '14px',
            border: '1px solid #e9ecef'
          }}>
            <button
              onClick={handleSave}
              disabled={isSaving}
              style={{
                padding: '12px 24px',
                backgroundColor: '#4caf50',
                color: 'white',
                border: 'none',
                borderRadius: '14px',
                cursor: isSaving ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 4px rgba(76, 175, 80, 0.3)'
              }}
              onMouseEnter={(e) => {
                if (!isSaving) {
                  e.currentTarget.style.backgroundColor = '#45a049';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isSaving) {
                  e.currentTarget.style.backgroundColor = '#4caf50';
                  e.currentTarget.style.transform = 'translateY(0)';
                }
              }}
            >
              <FontAwesomeIcon icon={faSave} />
              {isSaving ? 'Speichern...' : 'Speichern'}
            </button>
            <button
              onClick={handleCancel}
              disabled={isSaving}
              style={{
                padding: '12px 24px',
                backgroundColor: '#f44336',
                color: 'white',
                border: 'none',
                borderRadius: '14px',
                cursor: isSaving ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 4px rgba(244, 67, 54, 0.3)'
              }}
              onMouseEnter={(e) => {
                if (!isSaving) {
                  e.currentTarget.style.backgroundColor = '#d32f2f';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isSaving) {
                  e.currentTarget.style.backgroundColor = '#f44336';
                  e.currentTarget.style.transform = 'translateY(0)';
                }
              }}
            >
              <FontAwesomeIcon icon={faTimes} />
              Abbrechen
            </button>
          </div>
        )}
      </div>

      {/* Teams List */}
      {teams.length > 0 && (
        <div style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '14px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)', border: '1px solid #ececec',
          marginBottom: '20px'
        }}>
          <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#333' }}>
            <FontAwesomeIcon icon={faUsers} style={{ marginRight: '10px' }} />
            Trupps ({teams.length})
          </h3>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
            {teams.map((team) => {
              const isEmergencyTeam = team.emergencyTrupp === 1 || team.emergencyTrupp === '1' || team.emergencyTrupp === true;
              const displayName = isEmergencyTeam ? 'Notfalltrupp' : team.name;
              
              // Debug log - show all teams
              console.log('🚒 Team:', team.name, '| emergencyTrupp:', team.emergencyTrupp, '| type:', typeof team.emergencyTrupp, '| isEmergency:', isEmergencyTeam, '| Full team object:', team);
              
              return (
                <div key={team.id} style={{
                  padding: '15px',
                  border: isEmergencyTeam ? '2px solid #d32f2f' : '1px solid #ddd',
                  borderRadius: '14px',
                  backgroundColor: isEmergencyTeam ? '#ffebee' : '#f9f9f9'
                }}>
                  <h4 style={{ 
                    margin: '0 0 10px 0', 
                    color: isEmergencyTeam ? '#d32f2f' : '#333',
                    fontWeight: isEmergencyTeam ? 'bold' : 'normal'
                  }}>
                    {displayName}
                  </h4>
                  <div style={{ fontSize: '14px', color: '#666' }}>
                    <div><strong>Mitglieder:</strong> {team.membersString || 'Keine Daten'}</div>
                    {team.startdruck && <div><strong>Startdruck:</strong> {team.startdruck} bar</div>}
                    {team.funkkanal && <div><strong>Funkkanal:</strong> {team.funkkanal}</div>}
                    {team.standort && <div><strong>Standort:</strong> {team.standort}</div>}
                    {team.createdAt && (
                      <div>
                        <strong>Eingetreten:</strong> {formatApiDateTimeForDisplay(team.createdAt)}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default OperationOverview;
